<template>
  <button
    v-if="availableCount > 0"
    type="button"
    class="btn btn-sm btn-outline-warning nota-comercial-assign-btn flex-shrink-0 py-0 px-1"
    :disabled="busy"
    :title="tooltip"
    :aria-label="tooltip"
    @click.prevent.stop="assign"
  >
    <i class="fas fa-link me-1" aria-hidden="true"></i>
    <span>{{ buttonLabel }}</span>
    <span v-if="availableCount > 1" class="nota-comercial-assign-btn__count">{{ availableCount }}</span>
  </button>
</template>

<script>
import {
  fetchAvailableNotes,
  pickNotaComercialToApply,
  applyOfferedNotaComercial,
} from "../utils/offerNotaComercialOnOperationCreate";
import { clearNotaComercialPendingCache } from "../utils/notaComercialPendingContacts";

/**
 * Asignar una nota comercial que ya existe a esta operación.
 *
 * Va al lado del botón que crea una nota nueva: si el contacto tiene notas con saldo,
 * lo normal es aplicar una de esas en vez de emitir otra. Si no hay ninguna, el botón
 * no se muestra y queda solo el de crear.
 */
export default {
  name: "NotaComercialAssignButton",
  props: {
    /** Contacto dueño de las notas: el cliente o el proveedor. */
    contactId: {
      type: String,
      default: "",
    },
    /** De qué lado son las notas que se buscan. */
    side: {
      type: String,
      default: "client",
      validator: (v) => v === "client" || v === "supplier",
    },
    /** Operación a la que se aplica: 'proform' o 'shipping'. */
    targetType: {
      type: String,
      default: "proform",
      validator: (v) => v === "proform" || v === "shipping",
    },
    /** Id de esa operación. */
    targetId: {
      type: String,
      default: "",
    },
    /**
     * Gasto desde el que se asigna, si el botón vive en una línea de gastos.
     * Con esto la nota queda ligada a ese gasto y no al que diga su rol anterior.
     */
    expenseId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      availableCount: 0,
      busy: false,
    };
  },
  computed: {
    /**
     * Dice de quién son las notas: en un embarque conviven el botón del cliente y el del
     * proveedor, y con la misma etiqueta no hay forma de distinguirlos.
     */
    buttonLabel() {
      return this.side === "supplier"
        ? this.$t("notaComercial.assign.buttonSupplier")
        : this.$t("notaComercial.assign.buttonClient");
    },
    tooltip() {
      return this.$t("notaComercial.assign.tooltip", { count: this.availableCount });
    },
  },
  watch: {
    contactId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
    targetId() {
      this.load();
    },
  },
  methods: {
    async load() {
      const id = String(this.contactId || "").trim();
      if (!id || !String(this.targetId || "").trim()) {
        this.availableCount = 0;
        return;
      }
      const data = await fetchAvailableNotes(id, this.side);
      // Entre el pedido y la respuesta la fila pudo pasar a otro contacto.
      if (String(this.contactId || "").trim() !== id) return;
      this.availableCount = data && data.moduleActive ? data.notes.length : 0;
    },

    async assign() {
      if (this.busy) return;
      this.busy = true;
      try {
        // Se relee al abrir: el conteo puede tener minutos y el saldo pudo cambiar.
        const data = await fetchAvailableNotes(this.contactId, this.side);
        if (!data) return;
        this.availableCount = data.notes.length;
        if (!data.notes.length) return;

        const choice = await pickNotaComercialToApply(data.notes, this.side, (key) =>
          this.$t(key)
        );
        if (!choice || !choice.noteId) return;

        const ok = await applyOfferedNotaComercial(
          this.targetId,
          { ...choice, side: this.side, contactId: this.contactId },
          (key) => this.$t(key),
          { targetType: this.targetType, maxAttempts: 1, expenseId: this.expenseId }
        );
        if (!ok) return;

        clearNotaComercialPendingCache(this.contactId);
        await this.load();
        this.$emit("assigned", { ...choice, targetType: this.targetType, targetId: this.targetId });
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.nota-comercial-assign-btn {
  font-size: 0.7rem;
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  white-space: nowrap;
}

.nota-comercial-assign-btn__count {
  font-weight: 600;
}
</style>
